import {
  isDailyChallengeIdentity,
  type DailyChallengeIdentity,
} from "./daily-challenge-config";

export type SeasonalChallengeIdentity = `seasonal:v${number}:${string}`;
export type ShareableChallengeIdentity =
  DailyChallengeIdentity | SeasonalChallengeIdentity;

export interface ChallengeLinkTarget {
  readonly identity: ShareableChallengeIdentity;
  readonly kind: "daily" | "seasonal";
}

const snakelyAppUrl = "https://snakely.jlong.dev/";
const snakelyOrigin = new URL(snakelyAppUrl).origin;
const seasonalIdentityPattern =
  /^seasonal:v([1-9][0-9]*):[a-z0-9]+(?:-[a-z0-9]+)*$/u;

export const isSeasonalChallengeIdentity = (
  value: unknown,
): value is SeasonalChallengeIdentity => {
  if (typeof value !== "string") {
    return false;
  }
  const match = seasonalIdentityPattern.exec(value);
  return match !== null && Number.isSafeInteger(Number(match[1]));
};

const parseIdentity = (value: unknown): ChallengeLinkTarget | null => {
  if (isDailyChallengeIdentity(value)) {
    return { identity: value, kind: "daily" };
  }
  if (isSeasonalChallengeIdentity(value)) {
    return { identity: value, kind: "seasonal" };
  }
  return null;
};

export const isShareableChallengeIdentity = (
  value: unknown,
): value is ShareableChallengeIdentity => parseIdentity(value) !== null;

export const createChallengeLink = (
  identity: ShareableChallengeIdentity,
): string => {
  const target = parseIdentity(identity);
  if (target === null) {
    throw new Error("Challenge identity is invalid");
  }
  const url = new URL(snakelyAppUrl);
  url.searchParams.set("challenge", target.identity);
  return url.toString();
};

export const parseChallengeLink = (
  value: string,
): ChallengeLinkTarget | null => {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  if (
    url.origin !== snakelyOrigin ||
    url.pathname !== "/" ||
    url.hash !== "" ||
    [...url.searchParams.keys()].some((key) => key !== "challenge") ||
    url.searchParams.getAll("challenge").length !== 1
  ) {
    return null;
  }
  return parseIdentity(url.searchParams.get("challenge"));
};
